/**
 * UI compartilhada — Agendamento de Docas (#MT-B2FA396 Fase 3B/4).
 * Usado por Movimentação → Agendamento de Docas e Pedidos de Entrada.
 */
import api from '../api.js';
import db from '../../versa/db.js';
import formset from '../../versa/formset.js';
import { notify, ask } from '../../versa/msgbox.js';
import createForm from '../forms/input-dock-schedules.js';
import detailForm from '../forms/input-dock-schedule-detail.js';
import cancelForm from '../forms/input-dock-schedule-cancel.js';
import { resolveCompanyId } from './location-search.js';
import {
	statusLabel,
	dockTypeLabel,
	eventLabel,
	formatDate,
	formatDateBr,
	formatTime,
	canMutateStatus,
	MSG_FLAG_OFF,
	ACTIVE_STATUSES,
} from './input-dock-schedule-labels.js';

function escHtml(s)
{
	return String(s ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

function setText(root, sel, text)
{
	root?.querySelectorAll(sel)?.forEach((el) =>
	{
		el.textContent = text;
	});
}

function chipClass(state)
{
	if (state === 'AVAILABLE') return 'is-available';
	if (state === 'OCCUPIED') return 'is-occupied';
	if (state === 'OUT_OF_HOURS') return 'is-out';
	if (state === 'INCOMPATIBLE') return 'is-incompatible';
	return 'is-out';
}

function formatSlotLabel(startIso, endIso)
{
	return `${formatTime(startIso)}–${formatTime(endIso)}`;
}

function parseOrderId(raw)
{
	const m = String(raw ?? '').trim().match(/^(\d+)/);
	return m ? Number(m[1]) : NaN;
}

function normalizeNf(raw)
{
	return String(raw ?? '').trim().replace(/^0+(?=\d)/, '');
}

function nfMatches(orderNf, queryNf)
{
	const a = normalizeNf(orderNf);
	const b = normalizeNf(queryNf);
	if (!a || !b) return false;
	return a === b || a.includes(b) || b.includes(a);
}

function isEligibleOrder(order)
{
	const st = Number(order?.status);
	if (!Number.isFinite(st) || st >= 50)
		return false;
	if (ACTIVE_STATUSES.has(order?.dock_schedule_status))
		return false;
	return true;
}

function orderPickLabel(order, supplierNick)
{
	const bits = [
		`Pedido ${order.id}`,
		order.reference ? String(order.reference) : null,
		order.nf_number != null && order.nf_number !== '' ? `NF ${order.nf_number}` : null,
		order.volumes != null && order.volumes !== '' ? `${order.volumes} vol.` : null,
		supplierNick || null,
		order.dock_schedule_status ? statusLabel(order.dock_schedule_status) : 'Sem agenda',
	].filter(Boolean);
	return bits.join(' · ');
}

async function resolveUserId(raw)
{
	if (!raw) return null;
	const text = String(raw).trim();
	if (!text) return null;
	if (/^[0-9a-f-]{36}$/i.test(text)) return text;
	const users = await db.list('users').catch(() => []);
	const found = (users || []).find((u) =>
		u?.name === text || u?.username === text || String(u?.id) === text
	);
	return found?.id || null;
}

/**
 * Badge HTML para grade de Pedidos de Entrada.
 * @param {object} record
 * @returns {string}
 */
export function dockScheduleBadgeHtml(record)
{
	const id = record?.dock_schedule_id;
	const status = record?.dock_schedule_status;
	if (id == null || !status)
	{
		return '<span class="io-dock-badge io-dock-none" title="Sem agendamento de doca">Sem agenda</span>';
	}
	const tipParts = [
		record.dock_name ? `Doca ${record.dock_name}` : null,
		record.dock_schedule_start
			? `${formatDateBr(record.dock_schedule_start)} ${formatTime(record.dock_schedule_start)}–${formatTime(record.dock_schedule_end)}`
			: null,
		statusLabel(status),
	].filter(Boolean);
	const tip = escHtml(tipParts.join(' · '));
	const label = statusLabel(status);
	const cls = {
		SCHEDULED: 'io-dock-scheduled',
		IN_RECEIVING: 'io-dock-receiving',
		COMPLETED: 'io-dock-completed',
		CANCELLED: 'io-dock-cancelled',
		NO_SHOW: 'io-dock-noshow',
	}[status] || 'io-dock-other';
	const occ = record.dock_has_occurrence
		? ' <span class="io-dock-badge io-dock-occ" title="Com ocorrência">Com ocorrência</span>'
		: '';
	return `<span class="io-dock-badge ${cls}" title="${tip}">${escHtml(label)}</span>${occ}`;
}

/**
 * @param {string|null|undefined} status
 * @returns {boolean}
 */
export function hasActiveDockSchedule(status)
{
	return ACTIVE_STATUSES.has(status);
}

/**
 * @param {object} opts
 * @param {object} opts.payload — retorno GET schedule detail
 * @param {() => void|Promise<void>} [opts.onDone]
 * @param {(args: object) => void} [opts.onReschedule]
 * @param {(companyId: string, scheduleId: number) => void} [opts.onCancel]
 */
export function showScheduleDetail({ payload, onDone, onReschedule, onCancel } = {})
{
	if (!payload?.schedule)
		return notify('Agendamento não encontrado.', 'Agendamento de Docas');
	const schedule = payload.schedule;
	const companyId = schedule.company_id;
	const scheduleId = schedule.id;
	const fs = formset.create(detailForm, async (ev) =>
	{
		if (ev === 'Escape')
			fs.destroy();
	});
	fs.init();
	const root = document.querySelector('#input_dock_schedule_detail');
	if (!root) return;

	setText(root, '[data-sdp="id"]', String(schedule.id));
	setText(root, '[data-sdp="status"]', statusLabel(schedule.status));
	setText(root, '[data-sdp="title"]', `Agendamento #${schedule.id}`);

	const dock = payload.dock || {};
	const main = root.querySelector('#ids_detail_main');
	if (main)
	{
		main.innerHTML = [
			['Doca', dock.name || schedule.dock_id],
			['Tipo', dockTypeLabel(dock.dock_type)],
			['Armazém', schedule.warehouse_id],
			['Data', formatDate(schedule.scheduled_start)],
			['Horário', `${formatTime(schedule.scheduled_start)} – ${formatTime(schedule.scheduled_end)}`],
			['Duração', `${schedule.duration_minutes || '—'} min`],
			['Responsável', schedule.responsible_user_id || '—'],
			['Status', statusLabel(schedule.status)],
			['Ocorrência', schedule.has_occurrence ? 'Com ocorrência' : 'Sem ocorrência'],
		].map(([k, v]) => `<dt>${escHtml(k)}</dt><dd>${escHtml(v)}</dd>`).join('');
	}

	const orderBox = root.querySelector('#ids_detail_order');
	const order = payload.input_order || {};
	const supplier = payload.supplier || {};
	if (orderBox)
	{
		orderBox.innerHTML = [
			['Pedido Entrada', schedule.input_order_id],
			['NF', schedule.invoice_number || order.nf_number || '—'],
			['Volumes', schedule.volumes ?? '—'],
			['Fornecedor', supplier.nick || supplier.name || schedule.supplier_id || '—'],
			['Status pedido', order.status ?? '—'],
		].map(([k, v]) => `<dt>${escHtml(k)}</dt><dd>${escHtml(v)}</dd>`).join('');
	}
	const notesEl = root.querySelector('#ids_detail_notes');
	if (notesEl)
		notesEl.textContent = schedule.notes ? `Observações: ${schedule.notes}` : '';

	const evUl = root.querySelector('#ids_detail_events');
	const events = Array.isArray(payload.events) ? payload.events : [];
	if (evUl)
	{
		evUl.innerHTML = events.length
			? events.map((ev) => `
				<li>
					<div class="ids-tl-type">${escHtml(eventLabel(ev.event_type))}</div>
					<div class="ids-tl-when">${escHtml(ev.created_on || '')}</div>
				</li>`).join('')
			: '<li><div class="ids-tl-type">Sem eventos</div></li>';
	}

	const occs = Array.isArray(payload.occurrences) ? payload.occurrences : [];
	const occBlock = root.querySelector('#ids_detail_occ_block');
	const occUl = root.querySelector('#ids_detail_occs');
	if (occBlock && occUl)
	{
		if (occs.length)
		{
			occBlock.hidden = false;
			occUl.innerHTML = occs.map((o) => `
				<li>
					<div class="ids-tl-type">${escHtml(o.occurrence_type || o.type || 'Ocorrência')}</div>
					<div class="ids-tl-when">${escHtml(o.notes || o.created_on || '')}</div>
				</li>`).join('');
		}
		else
			occBlock.hidden = true;
	}

	const btnCancel = root.querySelector('#ids_detail_cancel');
	const btnReschedule = root.querySelector('#ids_detail_reschedule');
	const mutable = canMutateStatus(schedule.status);
	if (btnCancel) btnCancel.disabled = !mutable;
	if (btnReschedule) btnReschedule.disabled = !mutable;

	btnCancel?.addEventListener('click', () =>
	{
		if (!mutable)
			return notify('Agendamento não pode ser cancelado neste status.', 'Agendamento de Docas');
		fs.destroy();
		if (onCancel)
			onCancel(companyId, scheduleId);
		else
			openScheduleCancel({ companyId, scheduleId, onDone });
	});
	btnReschedule?.addEventListener('click', () =>
	{
		if (!mutable)
			return notify('Agendamento não pode ser reagendado neste status.', 'Agendamento de Docas');
		fs.destroy();
		const args = {
			mode: 'reschedule',
			companyId,
			scheduleId,
			schedule,
			dock,
			onDone,
		};
		if (onReschedule)
			onReschedule(args);
		else
			openScheduleEditor(args);
	});
}

/**
 * @param {{ companyId: string, scheduleId: number, onDone?: () => void|Promise<void> }} opts
 */
export function openScheduleCancel({ companyId, scheduleId, onDone } = {})
{
	const fs = formset.create(cancelForm, async (data) =>
	{
		const reason = String(data.cancel_reason || '').trim();
		if (!reason)
			return notify('Informe o motivo do cancelamento.', 'Agendamento de Docas');
		ask('Confirma o cancelamento deste agendamento?', 'Cancelar', async (answer) =>
		{
			if (answer !== 'Y') return;
			const resp = await api.call(
				api.cancel_input_dock_schedule,
				companyId,
				scheduleId,
				{ cancel_reason: reason },
			);
			if (!resp) return;
			notify('Agendamento cancelado.', 'Sucesso');
			fs.destroy();
			if (onDone) await onDone();
		});
	});
	fs.init();
}

/**
 * @param {object} opts
 * @param {'create'|'reschedule'} [opts.mode]
 * @param {string} [opts.companyId]
 * @param {string} [opts.companyNick]
 * @param {number} [opts.inputOrderId]
 * @param {number} [opts.scheduleId]
 * @param {object} [opts.schedule]
 * @param {object} [opts.dock]
 * @param {boolean} [opts.useOrderScopedCreate] — POST .../input-orders/{id}/dock-schedule
 * @param {() => void|Promise<void>} [opts.onDone]
 */
export function openScheduleEditor({
	mode = 'create',
	companyId,
	companyNick,
	inputOrderId,
	scheduleId,
	schedule,
	dock,
	useOrderScopedCreate = false,
	onDone,
} = {})
{
	const isReschedule = mode === 'reschedule';
	const selection = { dock_id: null, start: null, end: null, dock_name: null };

	let fs;
	try
	{
		fs = formset.create(createForm, async (data) =>
		{
			try
			{
				const company_id = await resolveCompanyId(data.company_id)
					|| data.company_id
					|| companyId
					|| schedule?.company_id;
				if (!company_id)
					return notify('Informe a empresa.', 'Agendamento de Docas');
				const input_order_id = parseOrderId(data.input_order_id || schedule?.input_order_id || inputOrderId);
				if (!isReschedule && (!Number.isFinite(input_order_id) || input_order_id < 1))
					return notify('Informe o Pedido de Entrada.', 'Agendamento de Docas');
				const duration_minutes = Number(data.duration_minutes);
				if (!Number.isFinite(duration_minutes) || duration_minutes <= 0)
					return notify('Informe a duração (maior que zero).', 'Agendamento de Docas');
				if (!selection.dock_id || !selection.start)
					return notify('Selecione um horário disponível.', 'Agendamento de Docas');

				const responsible_user_id = await resolveUserId(data.responsible_user_id);
				const notes = data.notes ? String(data.notes).trim() : null;
				let volumes = null;
				if (data.volumes !== undefined && data.volumes !== null && String(data.volumes).trim() !== '')
				{
					const n = Number(data.volumes);
					if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n))
						return notify('Volumes deve ser um número inteiro ≥ 0.', 'Agendamento de Docas');
					volumes = n;
				}
				const body = {
					dock_id: Number(selection.dock_id),
					scheduled_start: selection.start,
					duration_minutes,
					responsible_user_id,
					notes,
					volumes,
				};

				let resp;
				if (isReschedule)
				{
					resp = await api.call(
						api.reschedule_input_dock_schedule,
						company_id,
						Number(data.schedule_id || scheduleId || schedule?.id),
						body,
					);
				}
				else
				{
					if (useOrderScopedCreate || inputOrderId)
					{
						resp = await api.call(
							api.insert_input_order_dock_schedule,
							company_id,
							input_order_id,
							body,
						);
					}
					else
					{
						body.input_order_id = input_order_id;
						resp = await api.call(api.insert_input_dock_schedule, company_id, body);
					}
				}
				if (!resp) return;
				if (resp.warning)
					notify(String(resp.warning), 'Aviso');
				notify(
					isReschedule ? 'Agendamento reagendado.' : 'Agendamento criado.',
					'Sucesso',
				);
				fs.destroy();
				if (onDone) await onDone();
			}
			catch (e)
			{
				notify(e?.message || String(e), 'Agendamento de Docas');
			}
		});
	}
	catch (e)
	{
		notify(e?.message || String(e), 'Agendamento de Docas');
		console.error(e);
		return;
	}
	fs.init();

	const root = document.querySelector('#input_dock_schedule');
	if (!root) return;

	setText(root, '[data-sdp="title"]', isReschedule ? 'Reagendar agendamento' : 'Novo agendamento');
	setText(root, '[data-sdp="mode-badge"]', isReschedule ? 'Reagendar' : 'Criar');
	const saveBtn = root.querySelector('#ids_btn_save');
	if (saveBtn)
		saveBtn.textContent = isReschedule ? 'Confirmar reagendamento' : 'Confirmar agendamento';

	if (isReschedule && schedule)
	{
		root.querySelector('#company_id').value = companyNick || companyId || schedule.company_id || '';
		root.querySelector('#input_order_id').value = schedule.input_order_id || '';
		root.querySelector('#input_order_id').disabled = true;
		root.querySelector('#company_id').disabled = true;
		root.querySelector('#schedule_id').value = String(scheduleId || schedule.id || '');
		root.querySelector('#duration_minutes').value = schedule.duration_minutes || 60;
		if (schedule.scheduled_start)
			root.querySelector('#date').value = formatDate(schedule.scheduled_start);
		if (schedule.responsible_user_id)
			root.querySelector('#responsible_user_id').value = schedule.responsible_user_id;
		if (schedule.notes)
			root.querySelector('#notes').value = schedule.notes;
		if (schedule.invoice_number && root.querySelector('#nf_number'))
			root.querySelector('#nf_number').value = schedule.invoice_number;
		if (schedule.volumes != null && root.querySelector('#volumes'))
			root.querySelector('#volumes').value = String(schedule.volumes);
		setText(root, '[data-sdp="company"]', companyNick || companyId || schedule.company_id || '—');
		const btnList = root.querySelector('#ids_btn_list_orders');
		if (btnList) btnList.hidden = true;
		const btnNf = root.querySelector('#ids_btn_find_nf');
		if (btnNf) btnNf.hidden = true;
		if (root.querySelector('#nf_number'))
			root.querySelector('#nf_number').disabled = true;
	}
	else
	{
		const today = new Date();
		const ymd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
		if (!root.querySelector('#date').value)
			root.querySelector('#date').value = ymd;
		if (companyId || companyNick)
		{
			root.querySelector('#company_id').value = companyNick || companyId;
			root.querySelector('#company_id').disabled = !!inputOrderId;
			setText(root, '[data-sdp="company"]', companyNick || companyId);
		}
		if (inputOrderId)
		{
			root.querySelector('#input_order_id').value = String(inputOrderId);
			root.querySelector('#input_order_id').disabled = true;
			const btnList = root.querySelector('#ids_btn_list_orders');
			if (btnList) btnList.hidden = true;
		}
	}

	const updateConfirm = () =>
	{
		const box = root.querySelector('#ids_confirm');
		const txt = root.querySelector('#ids_confirm_text');
		if (!box || !txt) return;
		if (!selection.dock_id || !selection.start)
		{
			box.hidden = true;
			return;
		}
		box.hidden = false;
		txt.textContent = [
			`Doca ${selection.dock_name || selection.dock_id}`,
			`data ${formatDate(selection.start)}`,
			`${formatTime(selection.start)}–${formatTime(selection.end)}`,
			`${root.querySelector('#duration_minutes')?.value || ''} min`,
			`pedido ${root.querySelector('#input_order_id')?.value || ''}`,
			root.querySelector('#responsible_user_id')?.value
				? `responsável ${root.querySelector('#responsible_user_id').value}`
				: null,
		].filter(Boolean).join(' · ');
		root.querySelector('#dock_id').value = String(selection.dock_id);
		root.querySelector('#scheduled_start').value = selection.start;
		root.querySelector('#scheduled_end').value = selection.end || '';
	};

	const warehouseName = async (company_id, warehouse_id) =>
	{
		const list = await db.list('warehouses').catch(() => []) || [];
		const wh = list.find((w) =>
			String(w.company_id) === String(company_id)
			&& Number(w.id) === Number(warehouse_id)
		);
		return wh?.name || wh?.nick || (warehouse_id != null ? String(warehouse_id) : '—');
	};

	const supplierNick = async (company_id, supplier_id) =>
	{
		if (supplier_id == null) return null;
		const list = await db.list('suppliers').catch(() => []) || [];
		const s = list.find((x) =>
			String(x.company_id) === String(company_id)
			&& Number(x.id) === Number(supplier_id)
		);
		return s?.nick || s?.name || null;
	};

	const renderAvailability = async (payload) =>
	{
		const grid = root.querySelector('#ids_availability');
		const empty = root.querySelector('#ids_availability_empty');
		const warn = root.querySelector('#ids_order_warn');
		if (!grid || !empty) return;
		selection.dock_id = null;
		selection.start = null;
		selection.end = null;
		selection.dock_name = null;
		updateConfirm();

		const all = Array.isArray(payload?.docks) ? payload.docks : [];
		const docks = all.filter((d) => d.compatible !== false);
		const skipped = all.length - docks.length;
		if (warn)
		{
			const warnings = Array.isArray(payload?.warnings) ? payload.warnings : [];
			if (skipped > 0)
				warnings.push(`${skipped} doca(s) oculta(s): incompatível com o pedido.`);
			if (warnings.length)
			{
				warn.hidden = false;
				warn.textContent = warnings.join(' ');
			}
		}

		if (!docks.length)
		{
			grid.hidden = true;
			empty.hidden = false;
			empty.textContent = 'Nenhuma doca compatível com este pedido nesta data.';
			return;
		}
		empty.hidden = true;
		grid.hidden = false;
		const company_id = payload?.company_id;
		const cards = await Promise.all(docks.map(async (d) =>
		{
			const slots = Array.isArray(d.slots) ? d.slots : [];
			const chips = slots.length
				? slots.map((s) =>
				{
					const st = s.state || 'OUT_OF_HOURS';
					const disabled = st !== 'AVAILABLE';
					return `<button type="button" class="ids-chip ${chipClass(st)}"
						data-dock-id="${escHtml(d.dock_id)}"
						data-dock-name="${escHtml(d.name || '')}"
						data-start="${escHtml(s.start)}"
						data-end="${escHtml(s.end)}"
						data-state="${escHtml(st)}"
						${disabled ? 'disabled' : ''}>${escHtml(formatSlotLabel(s.start, s.end))}</button>`;
				}).join('')
				: '<span class="ids-chip is-out">Sem horário neste dia</span>';
			const whLabel = await warehouseName(company_id, d.warehouse_id);
			return `
				<article class="ids-av-card">
					<h4>${escHtml(d.name || `Doca ${d.dock_id}`)}</h4>
					<div class="ids-av-meta">
						<span class="ids-badge">${escHtml(dockTypeLabel(d.dock_type))}</span>
						<span>${escHtml(whLabel)}</span>
						<span>Ocupação ${escHtml(d.occupancy_pct ?? 0)}%</span>
					</div>
					<div class="ids-chip-row">${chips}</div>
				</article>`;
		}));
		grid.innerHTML = cards.join('');

		grid.querySelectorAll('.ids-chip.is-available').forEach((btn) =>
		{
			btn.addEventListener('click', () =>
			{
				grid.querySelectorAll('.ids-chip.is-selected').forEach((el) =>
					el.classList.remove('is-selected'));
				btn.classList.add('is-selected');
				selection.dock_id = Number(btn.dataset.dockId);
				selection.start = btn.dataset.start;
				selection.end = btn.dataset.end;
				selection.dock_name = btn.dataset.dockName || '';
				updateConfirm();
			});
		});
	};

	const loadVolumesFromInput = async (company_id, order_id) =>
	{
		try
		{
			const rows = await api.call(api.list_inputs, `?limit=50`) || [];
			const match = (Array.isArray(rows) ? rows : []).find((inp) =>
				String(inp.company_id) === String(company_id)
				&& String(inp.order_type || 'I') === 'I'
				&& Number(inp.order_id) === Number(order_id)
				&& inp.volumes != null
			);
			if (match?.volumes != null && root.querySelector('#volumes') && !root.querySelector('#volumes').value)
				root.querySelector('#volumes').value = String(Math.trunc(Number(match.volumes)));
		}
		catch (_)
		{
			/* opcional */
		}
	};

	const loadOrderSummary = async () =>
	{
		const company_id = await resolveCompanyId(root.querySelector('#company_id')?.value)
			|| root.querySelector('#company_id')?.value;
		const oid = parseOrderId(root.querySelector('#input_order_id')?.value);
		const box = root.querySelector('#ids_order_summary');
		const warn = root.querySelector('#ids_order_warn');
		if (warn) warn.hidden = true;
		if (!company_id || !Number.isFinite(oid) || oid < 1)
		{
			if (box) box.textContent = 'Informe a empresa e o Pedido de Entrada, ou liste os pedidos passíveis / busque por NF.';
			return;
		}
		if (root.querySelector('#input_order_id'))
			root.querySelector('#input_order_id').value = String(oid);
		setText(root, '[data-sdp="company"]', companyNick || company_id);
		const order = await api.call(api.select_input_order, company_id, oid);
		if (!order)
		{
			if (box) box.textContent = 'Pedido não encontrado.';
			return;
		}
		if (!isEligibleOrder(order) && !isReschedule)
		{
			if (box)
			{
				box.innerHTML = `<div>Pedido ${escHtml(oid)} já possui agendamento ativo ou está cancelado.</div>`;
			}
			return;
		}
		if (order.nf_number != null && order.nf_number !== '' && root.querySelector('#nf_number'))
			root.querySelector('#nf_number').value = String(order.nf_number);
		await loadVolumesFromInput(company_id, oid);
		const nick = await supplierNick(company_id, order.supplier_id);
		const volField = root.querySelector('#volumes')?.value;
		const lines = [
			`Pedido ${order.id}`,
			order.reference ? `Referência: ${order.reference}` : null,
			order.nf_number != null && order.nf_number !== '' ? `NF: ${order.nf_number}` : null,
			volField ? `Volumes: ${volField}` : null,
			nick ? `Fornecedor: ${nick}` : (order.supplier_id != null ? `Fornecedor (id): ${order.supplier_id}` : null),
			order.dock_schedule_status
				? `Agenda: ${statusLabel(order.dock_schedule_status)}`
				: 'Agenda: sem agenda ativa',
		].filter(Boolean);
		if (box) box.innerHTML = lines.map((l) => `<div>${escHtml(l)}</div>`).join('');
	};

	const renderEligibleList = async (company_id, eligible) =>
	{
		const box = root.querySelector('#ids_order_summary');
		const dl = root.querySelector('#eligible_input_orders');
		if (dl)
			dl.innerHTML = '';
		if (!eligible.length)
		{
			if (box) box.textContent = 'Nenhum pedido passível encontrado com os filtros informados.';
			return;
		}
		const items = await Promise.all(eligible.map(async (o) =>
		{
			const nick = await supplierNick(company_id, o.supplier_id);
			const label = orderPickLabel(o, nick);
			if (dl)
			{
				const opt = document.createElement('option');
				opt.value = String(o.id);
				opt.label = label;
				dl.appendChild(opt);
			}
			return { id: o.id, label, nf: o.nf_number };
		}));
		if (box)
		{
			box.innerHTML = `<div><strong>${items.length} pedido(s) passível(is)</strong> — clique para selecionar:</div>`
				+ `<ul class="ids-order-pick">${items.map((it) =>
					`<li><button type="button" data-order-id="${it.id}">${escHtml(it.label)}</button></li>`
				).join('')}</ul>`;
			box.querySelectorAll('[data-order-id]').forEach((btn) =>
			{
				btn.addEventListener('click', () =>
				{
					root.querySelector('#input_order_id').value = String(btn.dataset.orderId);
					void loadOrderSummary();
				});
			});
		}
	};

	const listEligibleOrders = async () =>
	{
		const company_id = await resolveCompanyId(root.querySelector('#company_id')?.value)
			|| root.querySelector('#company_id')?.value;
		if (!company_id)
			return notify('Informe a empresa para listar os pedidos.', 'Agendamento de Docas');
		const nfQ = normalizeNf(root.querySelector('#nf_number')?.value);
		const rows = await api.call(api.list_input_orders, '?limit=0') || [];
		let eligible = (Array.isArray(rows) ? rows : []).filter((o) =>
			String(o.company_id) === String(company_id) && isEligibleOrder(o)
		);
		if (nfQ)
			eligible = eligible.filter((o) => nfMatches(o.nf_number, nfQ));
		await renderEligibleList(company_id, eligible);
	};

	const findByNf = async () =>
	{
		const company_id = await resolveCompanyId(root.querySelector('#company_id')?.value)
			|| root.querySelector('#company_id')?.value;
		const nfQ = normalizeNf(root.querySelector('#nf_number')?.value);
		if (!company_id)
			return notify('Informe a empresa.', 'Agendamento de Docas');
		if (!nfQ)
			return notify('Informe o número da Nota Fiscal.', 'Agendamento de Docas');
		const rows = await api.call(api.list_input_orders, '?limit=0') || [];
		const eligible = (Array.isArray(rows) ? rows : []).filter((o) =>
			String(o.company_id) === String(company_id)
			&& isEligibleOrder(o)
			&& nfMatches(o.nf_number, nfQ)
		);
		if (!eligible.length)
			return notify('Nenhum pedido passível com essa NF nesta empresa.', 'Agendamento de Docas');
		if (eligible.length === 1)
		{
			root.querySelector('#input_order_id').value = String(eligible[0].id);
			await loadOrderSummary();
			return;
		}
		await renderEligibleList(company_id, eligible);
	};

	const searchAvailability = async () =>
	{
		const company_id = await resolveCompanyId(root.querySelector('#company_id')?.value)
			|| root.querySelector('#company_id')?.value;
		const oid = parseOrderId(root.querySelector('#input_order_id')?.value);
		const date = root.querySelector('#date')?.value;
		const duration_minutes = Number(root.querySelector('#duration_minutes')?.value);
		if (!company_id)
			return notify('Informe a empresa.', 'Agendamento de Docas');
		if (!Number.isFinite(oid) || oid < 1)
			return notify('Informe o Pedido de Entrada (ou liste os passíveis).', 'Agendamento de Docas');
		if (!date)
			return notify('Informe a data.', 'Agendamento de Docas');
		if (!Number.isFinite(duration_minutes) || duration_minutes <= 0)
			return notify('Informe a duração (maior que zero).', 'Agendamento de Docas');
		if (root.querySelector('#input_order_id'))
			root.querySelector('#input_order_id').value = String(oid);

		const q = new URLSearchParams();
		q.set('input_order_id', String(oid));
		q.set('date', date);
		q.set('duration_minutes', String(duration_minutes));

		const data = await api.call(
			api.list_input_dock_availability,
			company_id,
			`?${q.toString()}`,
		);
		if (!data)
		{
			const empty = root.querySelector('#ids_availability_empty');
			if (empty)
			{
				empty.hidden = false;
				empty.textContent = MSG_FLAG_OFF;
			}
			return;
		}
		await loadOrderSummary();
		await renderAvailability(data);
	};

	root.querySelector('#ids_btn_list_orders')?.addEventListener('click', () =>
	{
		void listEligibleOrders();
	});
	root.querySelector('#ids_btn_find_nf')?.addEventListener('click', () =>
	{
		void findByNf();
	});
	root.querySelector('#ids_btn_load_order')?.addEventListener('click', () =>
	{
		void loadOrderSummary();
	});
	root.querySelector('#ids_btn_availability')?.addEventListener('click', () =>
	{
		void searchAvailability();
	});

	if (isReschedule || inputOrderId)
		void loadOrderSummary();
}
